// NotificationContext.js - React context for notifications system
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

// Service Worker for Push Notifications
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const NotificationProvider = ({ children }) => {
  const { user, token, apiCall } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Check if push notifications are supported
  useEffect(() => {
    const checkPushSupport = () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        setPushSupported(true);
        checkPushSubscription();
      }
    };

    checkPushSupport();
  }, []);

  // Check current push subscription status
  const checkPushSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking push subscription:', error);
    }
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    if (!user || !token) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/notifications/?unread_only=${unreadOnly}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      } else {
        console.error('Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user, token, API_BASE]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user || !token) return;

    try {
      const response = await fetch(`${API_BASE}/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user, token, API_BASE]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    console.log('🔔 markAsRead function called with ID:', notificationId);
    console.log('🔑 Token available:', !!token);
    console.log('🌐 apiCall available:', !!apiCall);
    console.log('👤 User available:', !!user);

    if (!token || !apiCall) {
      console.error('❌ No token or apiCall available for marking notification as read');
      console.error('Token exists:', !!token);
      console.error('ApiCall exists:', !!apiCall);
      return false;
    }

    try {
      const url = `/notifications/${notificationId}/read`;
      console.log('🚀 Making PATCH request to:', url);

      const response = await apiCall(url, {
        method: 'PATCH'
      });

      console.log('✅ Response status:', response.status);
      console.log('✅ Response ok:', response.ok);

      if (response.ok) {
        console.log('🎉 Successfully marked notification as read');
        // Update local state
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId
              ? { ...notif, is_read: true, read_at: new Date().toISOString() }
              : notif
          )
        );

        // Update unread count
        if (notifications.find(n => n.id === notificationId && !n.is_read)) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }

        return true;
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to mark notification as read:', response.status, errorText);
        return false;
      }
    } catch (error) {
      console.error('💥 Error marking notification as read:', error);
      console.error('💥 Error details:', error.message);
      return false;
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    console.log('markAllAsRead called');
    console.log('Token available:', !!token);
    console.log('apiCall available:', !!apiCall);

    if (!token || !apiCall) {
      console.error('No token or apiCall available for marking all notifications as read');
      return false;
    }

    try {
      const url = `/notifications/mark-all-read`;
      console.log('Making PATCH request to:', url);

      const response = await apiCall(url, {
        method: 'PATCH'
      });

      console.log('markAllAsRead response status:', response.status);
      console.log('markAllAsRead response ok:', response.ok);

      if (response.ok) {
        console.log('Successfully marked all notifications as read');
        // Update local state
        setNotifications(prev =>
          prev.map(notif => ({
            ...notif,
            is_read: true,
            read_at: new Date().toISOString()
          }))
        );
        setUnreadCount(0);
        return true;
      } else {
        const errorText = await response.text();
        console.error('Failed to mark all notifications as read:', response.status, errorText);
        return false;
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    console.log('deleteNotification called for:', notificationId);

    if (!token || !apiCall) {
      console.error('No token or apiCall available for deleting notification');
      return false;
    }

    try {
      const url = `/notifications/${notificationId}`;
      console.log('Making DELETE request to:', url);

      const response = await apiCall(url, {
        method: 'DELETE'
      });

      console.log('deleteNotification response status:', response.status);
      console.log('deleteNotification response ok:', response.ok);

      if (response.ok) {
        console.log('Successfully deleted notification');

        // Update local state - remove the notification
        const wasUnread = notifications.find(n => n.id === notificationId && !n.is_read);
        setNotifications(prev => prev.filter(notif => notif.id !== notificationId));

        // Update unread count if the deleted notification was unread
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }

        return true;
      } else {
        const errorText = await response.text();
        console.error('Failed to delete notification:', response.status, errorText);
        return false;
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  };

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    if (!pushSupported || !user || !token) return false;

    try {
      // Register service worker
      let registration;
      try {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      } catch (error) {
        console.log('Service worker not available, push notifications disabled');
        return false;
      }

      // Get VAPID public key from server (you'd need to add this endpoint)
      const vapidResponse = await fetch(`${API_BASE}/notifications/vapid-public-key`);
      let vapidPublicKey;
      
      if (vapidResponse.ok) {
        const data = await vapidResponse.json();
        vapidPublicKey = data.public_key;
      } else {
        // Fallback - you'd set this in your environment
        vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      }

      if (!vapidPublicKey) {
        console.error('VAPID public key not available');
        return false;
      }

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const response = await fetch(`${API_BASE}/notifications/push/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.keys.p256dh))),
            auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.keys.auth)))
          }
        })
      });

      if (response.ok) {
        setPushSubscribed(true);
        return true;
      } else {
        await subscription.unsubscribe();
        return false;
      }
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return false;
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    if (!pushSupported) return false;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from server
        const response = await fetch(`${API_BASE}/notifications/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint
          })
        });

        // Unsubscribe locally regardless of server response
        await subscription.unsubscribe();
        setPushSubscribed(false);
        
        return response.ok;
      }
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  };

  // Load initial data when user logs in
  useEffect(() => {
    if (user && token) {
      fetchNotifications();
      fetchUnreadCount();
      
      // Set up periodic refresh for unread count
      const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    } else {
      // Clear data when user logs out
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, token, fetchNotifications, fetchUnreadCount]);

  // Add a notification (for real-time updates from WebSocket, etc.)
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.is_read) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  };

  // Show browser notification (for immediate feedback)
  const showBrowserNotification = (title, options = {}) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/logo64.svg',
        badge: '/logo64.svg',
        ...options
      });

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);

      return notification;
    }
    return null;
  };

  const contextValue = {
    // Data
    notifications,
    unreadCount,
    loading,
    pushSupported,
    pushSubscribed,

    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    subscribeToPush,
    unsubscribeFromPush,
    addNotification,
    requestNotificationPermission,
    showBrowserNotification,

    // Utilities
    refreshNotifications: () => {
      fetchNotifications();
      fetchUnreadCount();
    }
  };


  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export default NotificationContext;